import { useActivitiesQuery } from './api.js';

const KIND_STYLE: Record<string, { bg: string; fg: string; path: string; label: string }> = {
  call: { bg: '#E3F5F3', fg: '#0F6B63', label: 'Call', path: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2' },
  email: { bg: '#FEF3E2', fg: '#8A4B06', label: 'Email', path: 'M3 5h18v14H3z M3 7l9 6 9-6' },
  meeting: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Meeting', path: 'M3 10h18M16 3v4M8 3v4 M3 5h18v16H3z' },
  note: { bg: '#F1ECFD', fg: '#5B34B0', label: 'Note', path: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6' },
  created: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Created', path: 'M12 5v14M5 12h14' },
  assigned: { bg: '#F1ECFD', fg: '#5B34B0', label: 'Assigned', path: 'M12 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8' },
  converted: { bg: '#E6F4EC', fg: '#1E7A4C', label: 'Converted', path: 'm5 12 5 5 9-10' },
  stage_changed: { bg: '#E6F4EC', fg: '#1E7A4C', label: 'Stage changed', path: 'm5 12 5 5 9-10' },
  won: { bg: '#E6F4EC', fg: '#1E7A4C', label: 'Won', path: 'm5 12 5 5 9-10' },
  lost: { bg: '#FBE9E7', fg: '#B42318', label: 'Lost', path: 'M18 6 6 18M6 6l12 12' },
  pulse_outreach_sent: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Outreach sent', path: 'M3 5h18v14H3z M3 7l9 6 9-6' },
  pulse_reply_received: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Pulse reply received', path: 'M3.5 17.5h4l1.3-2.6 2 4.2 1.6-3.4 1.1 1.8h6.5' },
  pulse_reply_sent: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Reply sent', path: 'M3 5h18v14H3z M3 7l9 6 9-6' },
};

const GMAIL_LINKED_KINDS = new Set(['pulse_reply_received', 'pulse_outreach_sent', 'pulse_reply_sent']);

function gmailThreadUrl(metadata: Record<string, unknown>): string | null {
  const threadId = metadata.gmailThreadId;
  return typeof threadId === 'string' ? `https://mail.google.com/mail/u/0/#all/${threadId}` : null;
}

function groupLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

interface TimelineProps {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  opportunityId?: string;
  /** Lets a sent Inbox Pulse reply be resent — prefills the Email composer
   * with this activity's text rather than sending a silent duplicate. Only
   * leads currently have a composer wired to consume it. */
  onResend?: (bodyText: string) => void;
}

/** docs section 14 "Activities" — a real, if still growing, unified
 * timeline: every module writes into it as part of its own transaction
 * (docs/architecture/02 § events). Shared across lead/contact/company/deal
 * detail pages. Grouped by day (design/orion-crm-design/rendered-html/
 * LeadDetail.html's "Today"/"Yesterday" sections). */
export function Timeline({ onResend, ...filter }: TimelineProps) {
  const { data, isLoading } = useActivitiesQuery(filter);
  const items = data?.data ?? [];

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-text-muted">No activity yet.</p>;

  const groups: Array<{ label: string; items: typeof items }> = [];
  for (const item of items) {
    const label = groupLabel(item.occurredAt);
    const group = groups.at(-1);
    if (group?.label === label) group.items.push(item);
    else groups.push({ label, items: [item] });
  }

  return (
    <div className="flex flex-col gap-3.5">
      {groups.map((group) => (
        <div key={group.label} className="grid grid-cols-[68px_1fr] gap-2">
          <span className="text-[11px] font-semibold uppercase leading-6 tracking-[0.08em] text-ink">{group.label}</span>
          <div className="flex flex-col gap-3">
            {group.items.map((a) => {
              const style = KIND_STYLE[a.kind] ?? { bg: '#EFEEEA', fg: '#5E6470', label: a.kind, path: 'M12 8v4l3 2' };
              const gmailUrl = GMAIL_LINKED_KINDS.has(a.kind) ? gmailThreadUrl(a.metadata) : null;
              return (
                <div key={a.id} className="flex gap-2.5">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: style.bg }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d={style.path} />
                    </svg>
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex justify-between gap-2 text-[13px] leading-5">
                      <span>
                        <span className="font-semibold text-ink">{style.label}</span>
                        {a.relatedTo && <span className="text-text-secondary"> · {a.relatedTo.label}</span>}
                      </span>
                      <span className="shrink-0 text-xs text-text-muted">{new Date(a.occurredAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs leading-4.5 text-text-muted">
                      {a.actor?.displayName ?? 'System'}
                      {a.outcome && (
                        <span className="flex h-4.5 items-center rounded-full bg-success-soft px-1.5 font-semibold text-success">{a.outcome}</span>
                      )}
                      {gmailUrl && (
                        <a href={gmailUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">
                          Open in Gmail
                        </a>
                      )}
                      {onResend && a.kind === 'pulse_reply_sent' && a.bodyText && (
                        <button type="button" onClick={() => onResend(a.bodyText as string)} className="font-semibold text-primary hover:underline">
                          Resend
                        </button>
                      )}
                    </div>
                    {a.kind === 'call' && typeof a.metadata?.callId === 'string' && Boolean(a.metadata?.recordingUrl) && (
                      <audio controls preload="none" className="mt-1 h-8 w-full max-w-xs" src={`/api/v1/calling/calls/${a.metadata.callId}/recording`} />
                    )}
                    {a.bodyText && (
                      <p className="mt-1 whitespace-pre-wrap rounded-control border border-divider bg-ground px-2.5 py-2 text-[13px] leading-4.5 text-ink">{a.bodyText}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
